import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import styled, { css } from "styled-components";
import { Box } from "orcs-design-system";
import themeGet from "@styled-system/theme-get";
import PropTypes from "prop-types";
import { useAISidebar } from "src/hooks/useAISidebar";
import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import AIChatInterface from "../AIChatInterface";
import { AIChatProvider } from "../context/AIChatProviderWithAgentCore";
import { AGENTCORE_NAMES, SOURCE_TYPES } from "../consts";
import AIChatV3Interface from "./AIChatV3Interface";
import useResizableChatPaneWidth, {
  MIN_WIDTH,
  MAX_WIDTH,
} from "./hooks/useResizableChatPaneWidth";

const AIChatWindowWrapper = styled(Box)`
  position: relative;

  ${({ isOpen }) =>
    !isOpen &&
    css`
      display: none;
    `}

  ${({ aiViewMode }) =>
    aiViewMode === "fullscreen" &&
    css`
      flex: 1;
      padding: ${themeGet("space.r")};
    `}
`;

const DragHandle = styled.div`
  position: absolute;
  left: -3px;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  z-index: 20;
  &:hover {
    background: rgba(59, 130, 246, 0.3);
  }
`;

/**
 * Top-level AI chat window.
 *
 * v2 workspaces consume the unified `AIChatV3Provider` hoisted in AppLayout
 * (so the stream/thread survives navigation to the dashboard canvas). v1
 * workspaces keep their own per-window `AIChatProvider`.
 */
const AIChatV3Window = ({ isOpen }) => {
  const { closeAI } = useAISidebar();
  const { enableAskTeamformV2 } = useWorkspaceFeatureFlags();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const aiViewMode = query.get("aiViewMode");

  // v1 fallback owns its own agent state; v2 agent selection lives in the
  // hoisted provider.
  const [selectedAgent, setSelectedAgent] = useState(
    AGENTCORE_NAMES.ASK_TEAMFORM
  );

  const isSidebar = aiViewMode !== "fullscreen";
  const variantProp = isSidebar ? "sidebar" : "fullpage";

  const { width, setWidth } = useResizableChatPaneWidth();
  const dragStateRef = useRef(null);
  // Hold the active drag listeners so an unmount mid-drag (before mouseup) can
  // detach them instead of leaking handlers that keep calling setWidth.
  const dragListenersRef = useRef(null);

  const detachDragListeners = useCallback(() => {
    if (dragListenersRef.current) {
      const { onMove, onUp } = dragListenersRef.current;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      dragListenersRef.current = null;
    }
  }, []);

  const handleDragStart = useCallback(
    (e) => {
      e.preventDefault();
      dragStateRef.current = { startX: e.clientX, startWidth: width };

      const onMove = (moveEvent) => {
        if (!dragStateRef.current) {
          return;
        }
        // Pane is on the right edge; dragging left grows it.
        const delta = dragStateRef.current.startX - moveEvent.clientX;
        const next = Math.min(
          MAX_WIDTH,
          Math.max(MIN_WIDTH, dragStateRef.current.startWidth + delta)
        );
        setWidth(next);
      };

      const onUp = () => {
        dragStateRef.current = null;
        detachDragListeners();
      };

      dragListenersRef.current = { onMove, onUp };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [width, setWidth, detachDragListeners]
  );

  useEffect(() => detachDragListeners, [detachDragListeners]);

  const containerWidth = isSidebar ? `${width}px` : undefined;

  const interfaceProps = {
    variant: variantProp,
    showCloseButton: true,
    onClose: closeAI,
    headerTitle: "Ask TeamForm",
    showChatHistoryToggle: true,
    showNewChatButton: true,
  };

  return (
    <AIChatWindowWrapper
      aiViewMode={aiViewMode}
      isOpen={isOpen}
      width={containerWidth}
    >
      {isSidebar && isOpen && (
        <DragHandle onMouseDown={handleDragStart} aria-hidden="true" />
      )}
      {enableAskTeamformV2 ? (
        <AIChatV3Interface {...interfaceProps} />
      ) : (
        <AIChatProvider
          sourceId={SOURCE_TYPES.chat_q_and_a}
          selectedAgent={selectedAgent}
          setSelectedAgent={setSelectedAgent}
        >
          <AIChatInterface {...interfaceProps} />
        </AIChatProvider>
      )}
    </AIChatWindowWrapper>
  );
};

AIChatV3Window.propTypes = {
  isOpen: PropTypes.bool.isRequired,
};

export default AIChatV3Window;
